import React, { useCallback, useEffect, useState } from "react";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { DocumentViewerContent, FileViewerContent } from "./DocumentViewerContent";
import { oeffnenArt } from "./documentKind";
import { officeEditingService } from "../../services/officeEditingService";
import { useOfficeOpenMenu } from "./OfficeOpenMenuProvider";
import { ausgelagertesFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Öffnet eine beliebige Datei fullpage (maximiertes WindowManager-Fenster) —
 * X/Schließen legt die darunterliegende Ansicht wieder frei.
 *
 * Für Quellen ohne Dokument-Id (z. B. Mail-Anhänge): der Blob kommt über
 * `load()`. Für KMVS-Dokumente gibt es {@link useDocumentViewer}.
 */
export function useFileViewer() {
    const { openWindow } = useWindowManager();
    return useCallback(
        (file: {
            /** Stabile Fenster-Id, damit derselbe Anhang nicht doppelt aufgeht. */
            key: string;
            fileName: string;
            mimeType?: string | null;
            load: () => Promise<Blob>;
            onDownload?: () => void;
        }) => {
            openWindow({
                id: `file-viewer-${file.key}`,
                title: file.fileName,
                initialMaximized: true,
                // Aus einem ausgelagerten Fenster (Mail im eigenen Tab): die
                // Vorschau geht dort daneben auf, nicht im Hauptfenster.
                browserFensterNeben: ausgelagertesFenster(),
                render: () => (
                    <FileViewerContent
                        load={file.load}
                        fileName={file.fileName}
                        mimeType={file.mimeType}
                        onDownload={file.onDownload}
                    />
                ),
            });
        },
        [openWindow],
    );
}

/**
 * Was ein Aufrufer über ein Dokument weiß. Die optionalen Felder sind die eines
 * `DocumentDto` — wer ein DTO übergibt, liefert sie mit, ohne es zu wissen.
 */
export type DokumentZumOeffnen = {
    id: number;
    fileName: string;
    mimeType?: string | null;
    fileSize?: number | null;
    contactId?: number | null;
    vorgangId?: number | null;
    vorgang?: { id: number } | null;
};

/**
 * T-247: der Vorgangsbezug einer Mail-Datei für Antworten/Weiterleiten — direkt
 * oder über das To-do (`vorgang`), dazu Kunde und Größe.
 */
function mailKontextAus(doc: DokumentZumOeffnen) {
    return {
        vorgangId: doc.vorgangId ?? doc.vorgang?.id ?? null,
        contactId: doc.contactId ?? null,
        fileSize: doc.fileSize ?? null,
    };
}

/**
 * Öffnet ein KMVS-Dokument fullpage (maximiertes WindowManager-Fenster).
 */
export function useDocumentViewer() {
    const { openWindow, setWindowTitle } = useWindowManager();
    return useCallback(
        (doc: DokumentZumOeffnen, searchTerm?: string) => {
            const id = `document-viewer-${doc.id}`;
            const mailKontext = mailKontextAus(doc);
            openWindow({
                id,
                title: doc.fileName,
                initialMaximized: true,
                browserFensterNeben: ausgelagertesFenster(),
                // PDF und Bild zeigen direkt unter der Titelleiste die Kopfzeile
                // des Viewers/Editors — beide im selben, helleren Ton.
                titelleisteHell: ["pdf", "bild-editor"].includes(oeffnenArt(doc.fileName, doc.mimeType, { mitDokumentId: true })),
                render: () => (
                    <DocumentViewerContent
                        docId={doc.id}
                        fileName={doc.fileName}
                        mimeType={doc.mimeType}
                        searchTerm={searchTerm}
                        onRenamed={(name) => setWindowTitle(id, name)}
                        mailKontext={mailKontext}
                    />
                ),
            });
        },
        [openWindow, setWindowTitle],
    );
}

/**
 * Steht auf diesem Server ein Office-Editor bereit?
 *
 * Der Service bündelt Listenabfragen kurzzeitig. Vor dem tatsächlichen
 * Editoraufbau prüft OfficeDocumentView den aktuellen Provider erneut. Bewusst ein Hook und kein `await` in der Klick-Behandlung: sonst würde
 * aus `useOpenDocument` eine asynchrone Funktion, und jede Aufrufstelle müsste
 * ein Promise entgegennehmen, das sie nicht braucht.
 *
 * Beim allerersten Rendern ist die Antwort `false`. Ein Klick in genau diesem
 * Moment lädt herunter statt zu öffnen — der harmlosere der beiden Fehler.
 */
export function useOfficeEditingEnabled(): boolean {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        let alive = true;
        void officeEditingService.isEnabled().then((v) => alive && setEnabled(v));
        return () => {
            alive = false;
        };
    }, []);
    return enabled;
}

/**
 * Klick-Verhalten für eine Dokumentzeile: darstellbare Typen öffnen den Viewer,
 * alles andere lädt herunter. Bündelt die Entscheidung an einer Stelle, damit
 * Vorgang, Todo und Kundenakte sich gleich verhalten.
 *
 * Office-Dateien bieten zuerst die Zielwahl am Dokument an. Der Editor startet
 * erst nach Web-/Desktop-Klick; der Picker prüft die aktuelle Verfügbarkeit.
 */
export function useOpenDocument() {
    const openViewer = useDocumentViewer();
    const openOfficeMenu = useOfficeOpenMenu();
    return useCallback(
        (
            doc: DokumentZumOeffnen,
            opts?: { download?: (id: number) => void; searchTerm?: string; anchor?: HTMLElement },
        ) => {
            const art = oeffnenArt(doc.fileName, doc.mimeType, { mitDokumentId: true });
            if (art === "download") {
                opts?.download?.(doc.id);
                return;
            }
            if (art === "office") {
                openOfficeMenu(doc, opts?.anchor);
                return;
            }
            openViewer(doc, opts?.searchTerm);
        },
        [openViewer, openOfficeMenu],
    );
}
